import { buildEmptyCartSnapshot } from './utils';

const CART_COMMAND_NAMES = ['addItem', 'removeItem', 'open', 'close', 'sync', 'clearAfterCheckout'];

const createDeferred = () => {
    let resolvePromise;
    let rejectPromise;

    const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
    });

    return {
        promise,
        resolve: resolvePromise,
        reject: rejectPromise,
    };
};

const createBootstrapApi = (windowObject) => {
    const subscribers = new Set();
    const pendingSubscriptions = new Set();
    const queue = [];
    let snapshot = buildEmptyCartSnapshot();

    const bootstrapApi = {
        __crateCartBootstrap: true,
        __queue: queue,
        __subscribers: subscribers,
        __pendingSubscriptions: pendingSubscriptions,
        __setSnapshot(nextSnapshot) {
            snapshot = nextSnapshot;
            subscribers.forEach((listener) => {
                listener(snapshot);
            });
        },
        getSnapshot() {
            return snapshot;
        },
        subscribe(listener) {
            if (typeof listener !== 'function') {
                return () => {};
            }

            subscribers.add(listener);
            pendingSubscriptions.add(listener);
            listener(snapshot);

            return () => {
                subscribers.delete(listener);
                pendingSubscriptions.delete(listener);
            };
        },
    };

    CART_COMMAND_NAMES.forEach((commandName) => {
        bootstrapApi[commandName] = (...args) => {
            const deferred = createDeferred();
            queue.push({
                name: commandName,
                args,
                resolve: deferred.resolve,
                reject: deferred.reject,
            });

            return deferred.promise;
        };
    });

    windowObject.CrateCart = bootstrapApi;

    return bootstrapApi;
};

export const installCrateCartBootstrap = (windowObject = window) => {
    if (windowObject.CrateCart && windowObject.CrateCart.__crateCartBootstrap) {
        return windowObject.CrateCart;
    }

    if (windowObject.CrateCart && !windowObject.CrateCart.__crateCartBootstrap) {
        return windowObject.CrateCart;
    }

    return createBootstrapApi(windowObject);
};

export const attachCrateCartGlobalApi = (controller, windowObject = window) => {
    const bootstrapApi = installCrateCartBootstrap(windowObject);

    const realApi = {
        addItem: (...args) => controller.addItem(...args),
        removeItem: (...args) => controller.removeItem(...args),
        open: (...args) => controller.open(...args),
        close: (...args) => controller.close(...args),
        getSnapshot: (...args) => controller.getSnapshot(...args),
        sync: (...args) => controller.sync(...args),
        clearAfterCheckout: (...args) => controller.clearAfterCheckout(...args),
        subscribe: (...args) => controller.subscribe(...args),
    };

    windowObject.CrateCart = realApi;

    bootstrapApi.__pendingSubscriptions.forEach((listener) => {
        controller.subscribe(listener);
    });

    const queuedCommands = [...bootstrapApi.__queue];
    bootstrapApi.__queue.length = 0;

    queuedCommands.reduce((promiseChain, queuedCommand) => (
        promiseChain.then(async () => {
            try {
                const response = await realApi[queuedCommand.name](...queuedCommand.args);
                queuedCommand.resolve(response);
            } catch (error) {
                queuedCommand.reject(error);
            }
        })
    ), Promise.resolve());

    return realApi;
};
